import React, { useState, useEffect } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api, appUserAuth } from "../lib/api";
import { toast } from "sonner";
import {
  ClipboardText, ShieldCheck, Warning, CheckCircle, ArrowRight,
  Sparkle, Lock, UploadSimple, FilePdf, X,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const SEV = {
  critical: { color: "border-red-500", pill: "bg-red-500/15 text-red-300 border-red-500/40", icon: Warning, label: "Critical" },
  warning:  { color: "border-yellow-500", pill: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40", icon: Warning, label: "Warning" },
  optimize: { color: "border-emerald-500", pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", icon: Sparkle, label: "Optimize" },
};

const GOAL_OPTS = [
  { v: "income", label: "Retirement income" },
  { v: "legacy", label: "Legacy / heirs" },
  { v: "liquidity", label: "Liquidity / banking" },
  { v: "accumulation", label: "Accumulation / growth" },
  { v: "protection", label: "Family protection" },
];
const MARITAL = [
  { v: "single", label: "Single" },
  { v: "married", label: "Married" },
  { v: "divorced", label: "Divorced" },
  { v: "widowed", label: "Widowed" },
];
const HEALTH = [
  { v: "preferred_plus", label: "Preferred Plus" },
  { v: "preferred", label: "Preferred" },
  { v: "standard_plus", label: "Standard Plus" },
  { v: "standard", label: "Standard" },
  { v: "substandard", label: "Substandard" },
];
const RISK = [
  { v: "conservative", label: "Conservative" },
  { v: "moderate", label: "Moderate" },
  { v: "aggressive", label: "Aggressive" },
];

export default function PolicyAudit() {
  const email = appUserAuth.email() || "";
  const [mode, setMode] = useState("form"); // form | upload
  const [step, setStep] = useState("form"); // form | result | upload-result
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadContext, setUploadContext] = useState("");
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    age: 35, sex: "male", health_class: "standard",
    marital_status: "single", dependents: 0,
    annual_income: 75000, monthly_savings_capacity: 500,
    liquid_assets: 10000, retirement_assets: 25000,
    has_term_life: false, term_life_face_amount: 0, term_years_remaining: 0,
    has_permanent_life: false, permanent_life_cash_value: 0,
    has_annuity: false, annuity_account_value: 0,
    primary_goal: "income", retirement_target_age: 65, risk_tolerance: "moderate",
    concerns: "",
  });

  useEffect(() => {
    if (!email) return;
    (async () => {
      try {
        const r = await api.get(`/policy-audit/history?email=${encodeURIComponent(email)}`);
        setHistory(r.data?.audits || []);
      } catch { /* ignore */ }
    })();
  }, [email]);

  const submit = async () => {
    if (!email) { toast.error("Sign in first."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/policy-audit/submit", { ...form, email });
      setResult(r.data.audit);
      setStep("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't run your audit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitUpload = async () => {
    if (!email) { toast.error("Sign in first."); return; }
    if (!uploadFile) { toast.error("Pick a PDF or text file first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("email", email);
      fd.append("context", uploadContext || "");
      const r = await api.post("/policy-upload/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(r.data.analysis);
      setStep("upload-result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "We couldn't analyze that file. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="policy-audit-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      <section className="max-w-4xl mx-auto px-6 lg:px-12 pt-36 pb-8">
        <p className="eyebrow"><span className="gold-divider" />Full Policy Audit</p>
        <h1 data-testid="audit-headline" className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">
          {step === "form" ? <>Tell me your life. <span className="gold-text italic">I&apos;ll tell you the gaps.</span></>
                          : <>Your audit. <span className="gold-text italic">Score {result?.score}/100.</span></>}
        </h1>
        <p className="text-lg text-exodus-muted mt-6 max-w-2xl leading-relaxed">
          {step === "form"
            ? "A 3-minute snapshot of your income, existing policies, goals, and family situation. In return: a detailed audit showing every gap, in plain English, prioritized by urgency."
            : result?.summary}
        </p>
      </section>

      {step === "form" && (
        <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-24">
          {/* MODE TABS: form questionnaire vs upload existing policy */}
          <div className="grid grid-cols-2 border border-exodus-border mb-8">
            <button
              data-testid="audit-mode-form"
              onClick={() => setMode("form")}
              className={`px-6 py-4 text-left transition-colors ${mode === "form" ? "bg-exodus-gold text-exodus-bg" : "bg-exodus-surface hover:bg-exodus-elevated"}`}
            >
              <p className={`text-xs eyebrow ${mode === "form" ? "text-exodus-bg" : "text-exodus-muted"}`}>Option A</p>
              <p className="font-display text-lg mt-1">Answer questions</p>
              <p className={`text-xs mt-1 ${mode === "form" ? "text-exodus-bg/70" : "text-exodus-muted"}`}>Ideal if you don&apos;t have a policy yet.</p>
            </button>
            <button
              data-testid="audit-mode-upload"
              onClick={() => setMode("upload")}
              className={`px-6 py-4 text-left transition-colors ${mode === "upload" ? "bg-exodus-gold text-exodus-bg" : "bg-exodus-surface hover:bg-exodus-elevated"}`}
            >
              <p className={`text-xs eyebrow ${mode === "upload" ? "text-exodus-bg" : "text-exodus-muted"}`}>Option B</p>
              <p className="font-display text-lg mt-1">Upload your current policy</p>
              <p className={`text-xs mt-1 ${mode === "upload" ? "text-exodus-bg/70" : "text-exodus-muted"}`}>PDF or text — we analyze it for you.</p>
            </button>
          </div>

          {mode === "upload" ? (
            <div data-testid="policy-upload-panel" className="card-surface p-8 lg:p-10 space-y-6">
              <div>
                <p className="eyebrow text-exodus-muted">Upload your current policy</p>
                <h2 className="font-display text-2xl mt-2">Drop your policy document. We&apos;ll read every page.</h2>
                <p className="text-sm text-exodus-muted mt-3 leading-relaxed">
                  Upload the PDF version of your existing life insurance policy, annuity contract, or in-force illustration. We extract every clause, identify hidden fees, flag structural weaknesses, and hand you a plain-English summary. Nothing is shared outside your account and your advisor.
                </p>
              </div>

              {/* Drop zone */}
              <label htmlFor="policy-file-input" className={`block border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${uploadFile ? "border-exodus-gold bg-exodus-elevated" : "border-exodus-border hover:border-exodus-gold hover:bg-exodus-surface"}`}>
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-4">
                    <FilePdf size={40} weight="thin" className="text-exodus-gold shrink-0" />
                    <div className="text-left min-w-0">
                      <p className="font-display text-lg truncate">{uploadFile.name}</p>
                      <p className="text-xs text-exodus-muted">{(uploadFile.size / 1024).toFixed(1)} KB · ready to analyze</p>
                    </div>
                    <button
                      type="button"
                      data-testid="policy-file-remove"
                      onClick={(e) => { e.preventDefault(); setUploadFile(null); }}
                      className="w-8 h-8 inline-flex items-center justify-center border border-exodus-border hover:border-exodus-gold hover:text-exodus-gold transition-colors shrink-0"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <UploadSimple size={40} weight="thin" className="text-exodus-gold mx-auto mb-4" />
                    <p className="font-display text-xl">Tap to select your policy PDF</p>
                    <p className="text-xs text-exodus-muted mt-3">PDF, TXT, or MD. Up to 10MB. Scanned image PDFs may not extract — export a text-based version if possible.</p>
                  </div>
                )}
                <input
                  id="policy-file-input"
                  data-testid="policy-file-input"
                  type="file"
                  accept=".pdf,.txt,.md,application/pdf,text/plain"
                  className="sr-only"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
              </label>

              <div>
                <p className="text-xs eyebrow text-exodus-muted mb-2">Optional context (helps the analysis)</p>
                <textarea
                  data-testid="policy-upload-context"
                  rows={3}
                  value={uploadContext}
                  onChange={(e) => setUploadContext(e.target.value)}
                  placeholder="Anything the analyst should know? e.g. 'bought in 2018 during job change', 'employer-sponsored group policy', 'unsure if I still need it'"
                  className="w-full bg-exodus-bg border border-exodus-border p-4 text-sm text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors resize-none"
                />
              </div>

              <div className="pt-4 border-t border-exodus-border flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-exodus-muted">
                  <Lock size={12} className="text-exodus-gold" />
                  Your file is analyzed in-memory and stored encrypted. Never shared beyond your advisor.
                </div>
                <button
                  data-testid="policy-upload-submit"
                  onClick={submitUpload}
                  disabled={uploading || !uploadFile || !email}
                  className="btn-gold inline-flex items-center gap-3 disabled:opacity-40"
                >
                  {uploading ? "Analyzing… (30-60s)" : "Analyze my policy"}
                  {!uploading && <ArrowRight size={14} weight="bold" />}
                </button>
              </div>
            </div>
          ) : (
          <div className="card-surface p-8 lg:p-10 space-y-10">
            {/* IDENTITY */}
            <FieldGroup title="You">
              <Number testid="audit-age" label="Age" value={form.age} min={18} max={90} onChange={(v) => patch("age", v)} />
              <Radio testid="audit-sex" label="Sex" value={form.sex} options={[{v:"male",label:"Male"},{v:"female",label:"Female"}]} onChange={(v) => patch("sex", v)} />
              <Select testid="audit-health" label="Health class" value={form.health_class} options={HEALTH} onChange={(v) => patch("health_class", v)} />
              <Select testid="audit-marital" label="Marital status" value={form.marital_status} options={MARITAL} onChange={(v) => patch("marital_status", v)} />
              <Number testid="audit-dependents" label="Number of dependents" value={form.dependents} min={0} max={10} onChange={(v) => patch("dependents", v)} />
            </FieldGroup>

            {/* INCOME */}
            <FieldGroup title="Income &amp; savings">
              <Currency testid="audit-income" label="Annual income (gross)" value={form.annual_income} onChange={(v) => patch("annual_income", v)} />
              <Currency testid="audit-savings" label="Monthly amount you can direct to a policy" value={form.monthly_savings_capacity} onChange={(v) => patch("monthly_savings_capacity", v)} />
              <Currency testid="audit-liquid" label="Liquid assets (cash, brokerage, HYSA)" value={form.liquid_assets} onChange={(v) => patch("liquid_assets", v)} />
              <Currency testid="audit-retirement" label="Retirement assets (401k, IRA, Roth)" value={form.retirement_assets} onChange={(v) => patch("retirement_assets", v)} />
            </FieldGroup>

            {/* EXISTING COVERAGE */}
            <FieldGroup title="What you already have">
              <Toggle testid="audit-has-term" label="I have a term life policy" value={form.has_term_life} onChange={(v) => patch("has_term_life", v)} />
              {form.has_term_life && (
                <>
                  <Currency testid="audit-term-face" label="Term face amount" value={form.term_life_face_amount} onChange={(v) => patch("term_life_face_amount", v)} />
                  <Number testid="audit-term-years" label="Years remaining on the level period" value={form.term_years_remaining} min={0} max={40} onChange={(v) => patch("term_years_remaining", v)} />
                </>
              )}
              <Toggle testid="audit-has-perm" label="I have permanent life (whole life / IUL)" value={form.has_permanent_life} onChange={(v) => patch("has_permanent_life", v)} />
              {form.has_permanent_life && (
                <Currency testid="audit-perm-cv" label="Current cash value" value={form.permanent_life_cash_value} onChange={(v) => patch("permanent_life_cash_value", v)} />
              )}
              <Toggle testid="audit-has-annuity" label="I have an annuity" value={form.has_annuity} onChange={(v) => patch("has_annuity", v)} />
              {form.has_annuity && (
                <Currency testid="audit-annuity-av" label="Current annuity account value" value={form.annuity_account_value} onChange={(v) => patch("annuity_account_value", v)} />
              )}
            </FieldGroup>

            {/* GOALS */}
            <FieldGroup title="Where you want to end up">
              <Select testid="audit-goal" label="Primary goal" value={form.primary_goal} options={GOAL_OPTS} onChange={(v) => patch("primary_goal", v)} />
              <Number testid="audit-retire-age" label="Target retirement age" value={form.retirement_target_age} min={Math.max(form.age + 5, 50)} max={80} onChange={(v) => patch("retirement_target_age", v)} />
              <Select testid="audit-risk" label="Risk tolerance" value={form.risk_tolerance} options={RISK} onChange={(v) => patch("risk_tolerance", v)} />
              <div className="col-span-full">
                <p className="text-xs eyebrow text-exodus-muted mb-2">Anything specific you&apos;re worried about? (optional)</p>
                <textarea
                  data-testid="audit-concerns"
                  rows={3}
                  value={form.concerns}
                  onChange={(e) => patch("concerns", e.target.value)}
                  placeholder="Family history, business ownership, upcoming inheritance, tax questions..."
                  className="w-full bg-exodus-bg border border-exodus-border p-4 text-sm text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors resize-none"
                />
              </div>
            </FieldGroup>

            <div className="pt-4 border-t border-exodus-border flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-exodus-muted">
                <Lock size={12} className="text-exodus-gold" />
                Your answers are private. Only you and your advisor see the results.
              </div>
              <button
                data-testid="audit-submit"
                onClick={submit}
                disabled={submitting || !email}
                className="btn-gold inline-flex items-center gap-3 disabled:opacity-40"
              >
                {submitting ? "Running your audit…" : "Run my Full Policy Audit"}
                {!submitting && <ArrowRight size={14} weight="bold" />}
              </button>
            </div>
          </div>
          )}

          {history.length > 0 && mode === "form" && (
            <div className="mt-10">
              <p className="eyebrow text-exodus-muted mb-3">Your previous audits</p>
              <div className="space-y-2">
                {history.slice(0, 5).map((h, i) => (
                  <button
                    key={i}
                    data-testid={`audit-history-${i}`}
                    onClick={() => { setResult(h.client_view); setStep("result"); window.scrollTo({top:0,behavior:"smooth"}); }}
                    className="w-full flex items-center justify-between border border-exodus-border px-4 py-3 hover:border-exodus-gold hover:bg-exodus-elevated transition-colors text-left"
                  >
                    <div>
                      <p className="text-sm gold-text">Score {h.client_view.score}/100 · {h.client_view.score_label}</p>
                      <p className="text-xs text-exodus-muted mt-1">{new Date(h.created_at).toLocaleString()}</p>
                    </div>
                    <ArrowRight size={14} className="text-exodus-muted" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {step === "upload-result" && uploadResult && (
        <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-24">
          <div className="card-surface p-8 lg:p-10 space-y-6">
            <div>
              <p className="eyebrow gold-text">AI Policy Analysis</p>
              <h2 className="font-display text-3xl mt-2">Here&apos;s what your policy actually says.</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <UploadStat label="Policy type" value={uploadResult.policy_type || "Unknown"} />
              <UploadStat label="Face amount" value={uploadResult.face_amount || "Unknown"} />
              <UploadStat label="Annual premium" value={uploadResult.annual_premium || "Unknown"} />
            </div>
            <div className="border-t border-exodus-border pt-6">
              <p className="eyebrow text-xs text-exodus-muted mb-2">Summary</p>
              <p className="text-base leading-relaxed">{uploadResult.summary}</p>
            </div>
            {uploadResult.strengths?.length > 0 && (
              <div className="border-t border-exodus-border pt-6">
                <p className="eyebrow text-xs gold-text mb-3">Strengths</p>
                <ul className="space-y-2">
                  {uploadResult.strengths.map((s, i) => (
                    <li key={i} data-testid={`upload-strength-${i}`} className="text-sm flex gap-3 leading-relaxed">
                      <CheckCircle size={16} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" /><span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {uploadResult.weaknesses?.length > 0 && (
              <div className="border-t border-exodus-border pt-6">
                <p className="eyebrow text-xs text-red-400 mb-3">Weaknesses / hidden costs</p>
                <ul className="space-y-2">
                  {uploadResult.weaknesses.map((w, i) => (
                    <li key={i} data-testid={`upload-weakness-${i}`} className="text-sm flex gap-3 leading-relaxed">
                      <Warning size={16} weight="fill" className="text-red-400 shrink-0 mt-0.5" /><span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {uploadResult.recommendations?.length > 0 && (
              <div className="border-t border-exodus-gold-deep/40 pt-6">
                <p className="eyebrow text-xs gold-text mb-3">What to do next</p>
                <ol className="space-y-3">
                  {uploadResult.recommendations.map((r, i) => (
                    <li key={i} data-testid={`upload-rec-${i}`} className="text-sm flex gap-3 leading-relaxed">
                      <span className="font-display gold-text shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="pt-6 flex flex-wrap gap-3">
              <Link to="/reserve" className="btn-gold inline-flex items-center gap-2">
                Book my consult <ArrowRight size={14} weight="bold" />
              </Link>
              <button
                data-testid="upload-cta-rerun"
                onClick={() => { setStep("form"); setMode("upload"); setUploadFile(null); }}
                className="text-sm border border-exodus-border text-exodus-text hover:border-exodus-gold hover:text-exodus-gold transition-colors px-6 py-3"
              >
                Upload another policy
              </button>
            </div>
            <div className="flex items-start gap-3 text-xs text-exodus-muted leading-relaxed border-t border-exodus-border pt-4">
              <ShieldCheck size={14} className="text-exodus-gold shrink-0 mt-0.5" />
              <p>AI analysis is directional guidance based on the text extracted from your document. Not a substitute for a licensed advisor&apos;s review. Some scanned or image-only PDFs may not extract cleanly.</p>
            </div>
          </div>
        </section>
      )}

      {step === "result" && result && (
        <section className="max-w-4xl mx-auto px-6 lg:px-12 pb-24">
          <div className="card-surface p-8 lg:p-10">
            <div className="flex items-center gap-4 mb-6">
              <ScoreRing score={result.score} />
              <div>
                <p className="eyebrow gold-text text-xs">Your audit score</p>
                <p className="font-display text-4xl mt-1">{result.score}<span className="text-lg text-exodus-muted">/100</span></p>
                <p className="text-sm text-exodus-muted mt-1">{result.score_label}</p>
              </div>
            </div>

            {result.findings.length === 0 ? (
              <div className="text-center py-10">
                <CheckCircle size={48} weight="fill" className="text-emerald-400 mx-auto mb-4" />
                <p className="font-display text-2xl">Your portfolio looks clean.</p>
                <p className="text-exodus-muted mt-2">Nothing critical to fix. Every conversation from here is optimization.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {result.findings.map((f, i) => {
                  const s = SEV[f.severity] || SEV.optimize;
                  const IconC = s.icon;
                  return (
                    <div key={i} data-testid={`audit-finding-${f.id}`} className={`border-l-4 ${s.color} bg-exodus-elevated p-5`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <div className="flex items-center gap-2">
                          <IconC size={16} weight="fill" className="text-exodus-gold" />
                          <p className="text-xs eyebrow text-exodus-muted">{f.category}</p>
                        </div>
                        <span className={`text-[10px] eyebrow tracking-wider border ${s.pill} px-2 py-1`}>{s.label}</span>
                      </div>
                      <h3 className="font-display text-xl">{f.headline}</h3>
                      <p className="text-sm text-exodus-muted leading-relaxed mt-3">{f.finding}</p>
                      <div className="mt-4 pt-4 border-t border-exodus-border flex items-start gap-3">
                        <ClipboardText size={16} weight="fill" className="text-exodus-gold shrink-0 mt-0.5" />
                        <p className="text-sm text-exodus-text"><span className="eyebrow text-xs text-exodus-muted mr-2">Action:</span>{f.action}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-10 pt-6 border-t border-exodus-gold-deep/40">
              <p className="eyebrow gold-text mb-3">Your next step</p>
              <p className="text-base leading-relaxed">{result.next_step}</p>
              <div className="flex gap-3 mt-6 flex-wrap">
                <Link to="/reserve" data-testid="audit-cta-reserve" className="btn-gold inline-flex items-center gap-2">
                  Book my private consult <ArrowRight size={14} weight="bold" />
                </Link>
                <button
                  data-testid="audit-cta-rerun"
                  onClick={() => setStep("form")}
                  className="text-sm border border-exodus-border text-exodus-text hover:border-exodus-gold hover:text-exodus-gold transition-colors px-6 py-3"
                >
                  Run another audit
                </button>
              </div>
            </div>

            <div className="mt-10 flex items-start gap-3 text-xs text-exodus-muted leading-relaxed border-t border-exodus-border pt-6">
              <ShieldCheck size={14} className="text-exodus-gold shrink-0 mt-0.5" />
              <p>Audit findings are directional guidance based on your inputs and industry-standard planning benchmarks. Not tax, legal, or investment advice. Bindable recommendations require full underwriting through your advisor.</p>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
function FieldGroup({ title, children }) {
  return (
    <div>
      <p className="eyebrow text-exodus-muted mb-4">{title}</p>
      <div className="grid sm:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function Number({ testid, label, value, min, max, onChange }) {
  return (
    <div>
      <p className="text-xs eyebrow text-exodus-muted mb-2">{label}</p>
      <input
        data-testid={testid}
        type="number" min={min} max={max} value={value}
        onChange={(e) => onChange(parseInt(e.target.value || "0", 10))}
        className="w-full bg-exodus-bg border border-exodus-border px-4 py-3 text-base text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors"
      />
    </div>
  );
}
function Currency({ testid, label, value, onChange }) {
  return (
    <div>
      <p className="text-xs eyebrow text-exodus-muted mb-2">{label}</p>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-exodus-muted">$</span>
        <input
          data-testid={testid}
          type="number" min={0} value={value}
          onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
          className="w-full bg-exodus-bg border border-exodus-border pl-8 pr-4 py-3 text-base text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors"
        />
      </div>
    </div>
  );
}
function Radio({ testid, label, value, onChange, options }) {
  return (
    <div>
      <p className="text-xs eyebrow text-exodus-muted mb-2">{label}</p>
      <div className="grid grid-cols-2 border border-exodus-border" data-testid={testid}>
        {options.map((o) => (
          <button key={o.v} type="button"
            data-testid={`${testid}-${o.v}`}
            onClick={() => onChange(o.v)}
            className={`px-4 py-3 text-sm transition-colors ${value === o.v ? "bg-exodus-gold text-exodus-bg" : "bg-exodus-bg text-exodus-text hover:bg-exodus-elevated"}`}
          >{o.label}</button>
        ))}
      </div>
    </div>
  );
}
function Select({ testid, label, value, onChange, options }) {
  return (
    <div>
      <p className="text-xs eyebrow text-exodus-muted mb-2">{label}</p>
      <select
        data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-exodus-bg border border-exodus-border px-4 py-3 text-base text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );
}
function Toggle({ testid, label, value, onChange }) {
  return (
    <div className="col-span-full flex items-center gap-4">
      <button
        type="button" data-testid={testid}
        onClick={() => onChange(!value)}
        className={`w-11 h-6 border transition-colors relative shrink-0 ${value ? "bg-exodus-gold border-exodus-gold" : "bg-exodus-bg border-exodus-border"}`}
      >
        <span className={`absolute top-0.5 w-4 h-4 bg-exodus-bg transition-all ${value ? "left-6 bg-white" : "left-0.5"}`} />
      </button>
      <p className="text-sm text-exodus-text">{label}</p>
    </div>
  );
}
function ScoreRing({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 75 ? "#2b8054" : pct >= 50 ? "#b8860b" : "#c0392b";
  return (
    <div className="relative w-20 h-20 shrink-0">
      <svg viewBox="0 0 42 42" className="w-full h-full -rotate-90">
        <circle cx="21" cy="21" r="15.9" fill="none" stroke="#2a2a2a" strokeWidth="3" />
        <circle cx="21" cy="21" r="15.9" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display text-lg" style={{ color }}>{score}</span>
      </div>
    </div>
  );
}

function UploadStat({ label, value }) {
  return (
    <div className="border border-exodus-border p-4">
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className="font-display text-lg mt-1 gold-text truncate">{value}</p>
    </div>
  );
}
